import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CancelIcon from '@mui/icons-material/Cancel';
import PeopleIcon from '@mui/icons-material/People';
import { StatusChip } from './StatusChip';
import { StatChip } from './StatChip';
import { EmailPreview } from './EmailPreview';
import { RecipientsModal } from './RecipientsModal';
import { fetchScheduledEmailDetails, updateScheduledEmail, cancelScheduledEmail } from 'api/emailHistory/scheduledEmailsApi';
import { useNavigate } from 'react-router-dom';
import { ROUTE_EMAIL_SENDER } from 'constants/routerPath';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { convertServerTimeToUserTime, convertUserTimeToServerTime } from 'libs/utils/timezone';
import { formatDateTime } from 'utils/emailHistory/dateUtils';
import { useIsMobile } from 'hooks/useIsMobile';

/**
 * Parse date string - Handles both UTC timestamps and server time strings
 */
const parseDate = (serverTimeString) => {
  if (!serverTimeString) return null;

  try {
    // Check if it's a UTC ISO timestamp (ends with 'Z' or has milliseconds)
    if (serverTimeString.includes('Z') || serverTimeString.includes('.')) {
      // It's a UTC timestamp - parse directly
      const parsed = dayjs(serverTimeString);
      return parsed.isValid() ? parsed : null;
    } else {
      // It's a server time string - convert to user's local time
      const userLocalDate = convertServerTimeToUserTime(serverTimeString);
      return dayjs(userLocalDate);
    }
  } catch (error) {
    console.error('Error parsing server time:', serverTimeString, error);
    const fallback = dayjs(serverTimeString);
    return fallback.isValid() ? fallback : null;
  }
};

/**
 * @component ExpandedRowScheduled
 * @description Expanded row content for scheduled email details
 * @param {Object} props
 * @param {number} props.scheduleId - Scheduled email ID (ScheduleID)
 * @param {function} props.onRefresh - Callback to refresh the list after updates
 * @returns {JSX.Element}
 */
export function ExpandedRowScheduled({ scheduleId, onRefresh }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recipientsModalOpen, setRecipientsModalOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const loadDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchScheduledEmailDetails(scheduleId);
        setDetails(data);
        
        // Initialize reschedule date
        const parsedDate = parseDate(data.schedule_date);
        setRescheduleDate(parsedDate || dayjs());
      } catch (err) {
        console.error('Error fetching scheduled email details:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadDetails();
  }, [scheduleId]);

  // Check if email can be edited/rescheduled (must be Pending and not within 1 hour)
  const canEdit = details?.status === 'Pending';
  const canReschedule = details?.status === 'Pending';
  const canCancel = details?.status === 'Pending';

  // Check time constraint: if scheduled within 1 hour, disable edit/reschedule
  const scheduleDate = details?.schedule_date ? new Date(details.schedule_date) : null;
  const oneHourFromNow = new Date(Date.now() + 60 * 60 * 1000);
  const isWithinOneHour = scheduleDate && scheduleDate <= oneHourFromNow;
  const canEditDueToTime = canEdit && !isWithinOneHour;
  const canRescheduleDueToTime = canReschedule && !isWithinOneHour;

  const handleEdit = () => {
    // Store scheduled email data in sessionStorage for EmailSender to pick up
    // This matches the pattern used by the old CampaignHistoryModal
    try {
      sessionStorage.setItem('editScheduledEmail', JSON.stringify(details));
      navigate(ROUTE_EMAIL_SENDER);
    } catch (error) {
      console.error('Error storing scheduled email data:', error);
      setToast({ open: true, message: 'Failed to navigate to editor. Please try again.', severity: 'error' });
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleDate || !details) return;

    setUpdating(true);
    try {
      // Convert user time to server time
      const serverTimeString = convertUserTimeToServerTime(rescheduleDate.toDate());
      
      const result = await updateScheduledEmail(scheduleId, {
        schedule_date: serverTimeString,
      });

      // Note: Update creates a new schedule_id, but we'll refresh the list
      if (onRefresh) {
        onRefresh();
      }
      
      setRescheduleDialogOpen(false);
      setToast({ open: true, message: 'Email rescheduled successfully', severity: 'success' });
    } catch (err) {
      console.error('Error rescheduling email:', err);
      setToast({ open: true, message: err.message || 'Failed to reschedule email', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = async () => {
    setUpdating(true);
    try {
      await cancelScheduledEmail(scheduleId);
      
      if (onRefresh) {
        onRefresh();
      }
      
      setCancelDialogOpen(false);
      setToast({ open: true, message: 'Scheduled email cancelled successfully', severity: 'success' });
    } catch (err) {
      console.error('Error cancelling email:', err);
      setToast({ open: true, message: err.message || 'Failed to cancel email', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, bgcolor: 'grey.50', textAlign: 'center' }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Loading email details...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, bgcolor: 'grey.50' }}>
        <Alert severity="error">
          Failed to load details: {error}
        </Alert>
      </Box>
    );
  }

  if (!details) {
    return null;
  }

  const recipientCount = details.contact_count || (details.list_id === 0 ? 1 : 0);
  // Use list_name or ListName from details if available (handle both snake_case and PascalCase)
  const campaignName = details.list_name || details.ListName || 
    (details.list_id === 0 ? 'Individual Email' : `Campaign ${details.list_id}`);

  return (
    <Box sx={{ p: 3, backgroundColor: 'grey.50' }}>
      <Grid container spacing={3}>
        {/* Left Side: Email Preview */}
        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="subtitle1" fontWeight="600">
              Email Preview
            </Typography>
          </Box>
          <Paper variant="outlined" sx={{ minHeight: isMobile ? 250 : 350 }}>
            <EmailPreview htmlContent={details.email_contents} />
          </Paper>
        </Grid>

        {/* Right Side: Details & Actions */}
        <Grid item xs={12} md={6}>
          {/* Schedule Details */}
          <Typography variant="subtitle2" gutterBottom>
            Schedule Details
          </Typography>
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleIcon fontSize="small" color="action" />
              <Typography variant="body2">
                Scheduled: {formatDateTime(details.schedule_date)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">Status:</Typography>
              <StatusChip status={details.status} type="scheduled" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon fontSize="small" color="action" />
              <Typography variant="body2">
                Recipients: {recipientCount}
              </Typography>
            </Box>
            {details.campaignName && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  Campaign: {details.campaignName}
                </Typography>
              </Box>
            )}
          </Stack>

          {/* Email Details */}
          <Typography variant="subtitle2" gutterBottom>
            Email Details
          </Typography>
          <Stack spacing={0.5} sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              From: {details.from_email}
            </Typography>
            {details.reply_to_email && (
              <Typography variant="body2" color="text.secondary">
                Reply-To: {details.reply_to_email}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Subject: {details.subject}
            </Typography>
          </Stack>

          {/* Action Buttons */}
          <Typography variant="subtitle2" gutterBottom>
            Actions
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              flexWrap: 'wrap',
              gap: 2,
              mb: 2,
            }}
          >
            {details.list_id > 0 && (
              <Button
                variant="outlined"
                startIcon={<PeopleIcon />}
                onClick={() => setRecipientsModalOpen(true)}
                fullWidth={isMobile}
              >
                View Recipients
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEdit}
              disabled={!canEditDueToTime}
              fullWidth={isMobile}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              startIcon={<ScheduleIcon />}
              onClick={() => setRescheduleDialogOpen(true)}
              disabled={!canRescheduleDueToTime}
              fullWidth={isMobile}
            >
              Reschedule
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => setCancelDialogOpen(true)}
              disabled={!canCancel}
              fullWidth={isMobile}
            >
              Cancel
            </Button>
          </Box>

          {/* Time constraint warning */}
          {!canEditDueToTime && canEdit && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This email cannot be edited as it is scheduled to send within the next hour.
            </Alert>
          )}

          {/* Status constraint warning */}
          {!canEdit && details.status !== 'Pending' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This email cannot be modified because it is {details.status.toLowerCase()}.
            </Alert>
          )}
        </Grid>
      </Grid>

      {/* Recipients Modal */}
      {details.list_id > 0 && (
        <RecipientsModal
          open={recipientsModalOpen}
          onClose={() => setRecipientsModalOpen(false)}
          listId={details.list_id}
          readOnly
        />
      )}

      {/* Reschedule Dialog */}
      <Dialog
        open={rescheduleDialogOpen}
        onClose={() => !updating && setRescheduleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reschedule Email</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="New Schedule Date"
                value={rescheduleDate}
                onChange={setRescheduleDate}
                minDateTime={dayjs()}
                ampm={false}
                renderInput={(params) => (
                  <TextField {...params} fullWidth />
                )}
              />
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescheduleDialogOpen(false)} disabled={updating}>
            Cancel
          </Button>
          <Button
            onClick={handleReschedule}
            variant="contained"
            disabled={updating || !rescheduleDate}
          >
            {updating ? 'Rescheduling...' : 'Reschedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !updating && setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Scheduled Email?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently cancel this scheduled email? This action cannot be undone.
          </Typography>
          {details && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Subject: {details.subject}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Scheduled: {formatDateTime(details.schedule_date)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={updating}>
            No
          </Button>
          <Button
            onClick={handleCancel}
            variant="contained"
            color="error"
            disabled={updating}
          >
            {updating ? 'Cancelling...' : 'Yes, Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setToast({ ...toast, open: false })} 
          severity={toast.severity} 
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

